import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";

import AsesoriaIcon from "../../static/media/home/Asesoria.webp";
import ProvicionIcon from "../../static/media/home/Provisión.webp";
import LogisticaIcon from "../../static/media/home/Logística.webp";
import MaterialesIcon from "../../static/media/home/Materiales.webp";

const C = {
  dark: "#0C1F2F",
  navy: "#0057A8",
  navyDark: "#003D73",
  gold: "#FFCD00",
  goldDark: "#E6B800",
  teal: "#5BC8AF",
  gray: "#F5F7FA",
  white: "#ffffff",
};

const VALUE_PROPS = [
  {
    id: 1,
    title: "Asesoría Técnica Especializada",
    description:
      "No solo despachamos materiales; nuestro equipo técnico te asesora en la selección exacta de componentes para optimizar el presupuesto y la seguridad de tus proyectos eléctricos.",
    img: AsesoriaIcon,
    color: C.navy,
  },
  {
    id: 2,
    title: "Provisión Integral para Proyectos",
    description:
      "Desde instalaciones residenciales hasta infraestructura industrial. Encuentra todo el catálogo de cables, iluminación, automatización y ferretería en un solo aliado estratégico.",
    img: ProvicionIcon,
    color: C.gold,
  },
  {
    id: 3,
    title: "Logística Eficiente y Entregas en Obra",
    description:
      "Entendemos que el tiempo es dinero en tus proyectos. Garantizamos despachos rápidos con cobertura a nivel nacional y entregas directas en tu punto de trabajo.",
    img: LogisticaIcon,
    color: C.teal,
  },
  {
    id: 4,
    title: "Materiales Certificados con Garantía",
    description:
      "Tu seguridad es primero. Trabajamos exclusivamente con marcas aliadas reconocidas que cumplen con las normativas eléctricas vigentes, respaldando tu inversión con garantías reales.",
    img: MaterialesIcon,
    color: C.navy,
  },
];

const card = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

export default function ValueProposition() {
  return (
    <Box
      id="propuesta-de-valor"
      sx={{
        background: C.gray,
        pt: { xs: "72px", md: "96px" },
        pb: 0,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          maxWidth: "1280px",
          mx: "auto",
          px: { xs: "20px", sm: "32px", md: "40px" },
          boxSizing: "border-box",
          width: "100%",
        }}
      >
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          sx={{ textAlign: "center", mb: { xs: 5, md: 8 } }}
        >
          <Typography
            variant="h2"
            sx={{
              color: C.dark,
              fontWeight: 900,
              fontSize: { xs: 26, sm: 32, md: 42 },
              lineHeight: 1.12,
              letterSpacing: "-.02em",
            }}
          >
            Nuestra Propuesta de Valor
          </Typography>
        </Box>

        <Swiper
          modules={[Autoplay]}
          loop={true}
          spaceBetween={20}
          autoplay={{
            delay: 3500,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }}
          breakpoints={{
            0: { slidesPerView: 1.08 },
            600: { slidesPerView: 2 },
            1200: { slidesPerView: 4 },
          }}
        >
          {VALUE_PROPS.map((prop) => (
            <SwiperSlide key={prop.id}>
              <Box
                component={motion.div}
                variants={card}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                whileHover={{ y: -8, transition: { duration: 0.22 } }}
                sx={{
                  background: C.white,
                  borderRadius: "20px",
                  border: "1px solid rgba(0,87,168,.07)",
                  boxShadow: "0 4px 20px rgba(0,0,0,.06)",
                  height: { xs: 340, sm: 350, md: 360 },
                  display: "flex",
                  flexDirection: "column",
                  alignItems: { xs: "center", md: "flex-start" },
                  textAlign: { xs: "center", md: "left" },
                  p: { xs: "22px 18px", md: "26px 22px" },
                  position: "relative",
                  overflow: "hidden",
                  cursor: "pointer",
                  transition: "box-shadow .3s",
                  "&:hover": { boxShadow: "0 20px 44px rgba(0,0,0,.12)" },
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    width: "100%",
                    height: "4px",
                    background: prop.color,
                    transform: "scaleX(0.2)",
                    transition: "transform 0.35s ease",
                  },
                  "&:hover::after": { transform: "scaleX(1)" },
                }}
              >
                <Box
                  sx={{
                    width: { xs: 58, md: 66 },
                    height: { xs: 58, md: 66 },
                    borderRadius: "14px",
                    background: `${prop.color}16`,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    p: 1.2,
                    mb: 2,
                    flexShrink: 0,
                  }}
                >
                  <Box
                    component="img"
                    src={prop.img}
                    alt={prop.title}
                    sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                  />
                </Box>
                <Typography
                  sx={{
                    color: C.dark,
                    fontWeight: 800,
                    fontSize: { xs: 17, md: 19 },
                    mb: 1,
                    lineHeight: 1.25,
                  }}
                >
                  {prop.title}
                </Typography>
                <Typography
                  sx={{
                    color: "#5c6c7a",
                    fontSize: { xs: 13, md: 13.5 },
                    lineHeight: 1.65,
                    flexGrow: 1,
                  }}
                >
                  {prop.description}
                </Typography>
              </Box>
            </SwiperSlide>
          ))}
        </Swiper>
      </Box>

      {/* Transición Suavizada y extendida */}
      <Box
        sx={{
          height: { xs: "120px", md: "180px" },
          background: `linear-gradient(180deg, ${C.gray} 0%, ${C.navy} 100%)`,
          mt: { xs: 7, md: 10 },
        }}
      />

      <style>{`
        .swiper { overflow: visible; padding-top: 10px; padding-bottom: 18px; }
        .swiper-slide { height: auto; display: flex; }
        .swiper-slide > div { width: 100%; }
        @media (max-width: 900px) {
          .swiper { overflow: hidden; padding-top: 14px; padding-bottom: 22px; }
          .swiper-slide { padding: 0 5px; }
        }
      `}</style>
    </Box>
  );
}
